import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useApp } from "../context/AppContext";
import { Search, X } from "lucide-react";

export default function Shop() {
  const { API } = useApp();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  const load = useCallback((nextCat, nextSearch) => {
    const params = {};
    if (nextCat && nextCat !== "All") params.category = nextCat;
    if (nextSearch) params.q = nextSearch;
    axios.get(`${API}/products`, { params }).then(r => setProducts(r.data)).catch(err => console.error("Load products failed:", err));
  }, [API]);

  useEffect(() => { load(cat, activeSearch); }, [cat, activeSearch, load]);
  useEffect(() => { axios.get(`${API}/products/categories`).then(r => setCats(["All", ...r.data.categories])).catch(()=>{}); }, [API]);

  const pickCategory = (c) => {
    // Clear any active search when switching category — avoids "All appears empty" confusion
    setQ("");
    setActiveSearch("");
    setCat(c);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setActiveSearch(q.trim());
  };

  const clearSearch = () => {
    setQ("");
    setActiveSearch("");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="shop-page">
      <h1 className="font-display text-5xl mb-2">THE WAREHOUSE</h1>
      <p className="font-mono text-sm text-[var(--ink-2)] mb-8">ALL INVENTORY · FIRST COME, FIRST SERVED</p>

      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <form onSubmit={submitSearch} className="flex-1 flex gap-2" data-testid="search-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" size={18}/>
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search inventory..." className="input-field pl-10 pr-10" data-testid="search-input"/>
            {q && (
              <button type="button" onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 p-1" aria-label="Clear" data-testid="clear-search-btn"><X size={16}/></button>
            )}
          </div>
          <button type="submit" className="btn-primary" data-testid="search-btn">GO</button>
        </form>
      </div>

      {activeSearch && (
        <div className="mb-4 font-mono text-sm flex items-center gap-3" data-testid="active-search-chip">
          <span>Searching: <strong>"{activeSearch}"</strong></span>
          <button onClick={clearSearch} className="btn-outline py-1 px-2 text-xs" data-testid="clear-chip-btn">CLEAR ×</button>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-8" data-testid="category-filters">
        {cats.map(c => (
          <button key={c} onClick={()=>pickCategory(c)}
            className={`px-4 py-2 font-mono text-xs uppercase tracking-wider border-2 border-[var(--ink)] ${cat===c?'bg-[var(--ink)] text-[var(--paper)]':'bg-[var(--paper)]'}`}
            data-testid={`cat-${c}`}>{c}</button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="product-grid">
        {products.map(p => (
          <Link to={`/product/${p.product_id}`} key={p.product_id} className="card-industrial block relative" data-testid={`product-${p.product_id}`}>
            {p.original_price && p.original_price > p.price && (
              <div className="absolute top-3 right-3 bg-[var(--accent)] text-white font-display text-xs px-2 py-1 z-10 border-2 border-[var(--ink)]">SALE</div>
            )}
            <div className="aspect-[4/3] overflow-hidden bg-[var(--muted)] border-b-2 border-[var(--ink)]">
              <img src={p.images?.[0]} className="w-full h-full object-cover" alt={p.name}/>
            </div>
            <div className="p-4">
              <div className="font-mono text-xs text-[var(--ink-2)] mb-1">{p.sku}</div>
              <div className="font-display text-base leading-tight mb-2 min-h-[48px]">{p.name}</div>
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-2">
                  {p.original_price && p.original_price > p.price && <span className="font-mono text-xs line-through text-[var(--ink-2)]">${p.original_price.toFixed(2)}</span>}
                  <div className="font-display text-xl">${p.price.toFixed(2)}</div>
                </div>
                <div className={`font-mono text-xs ${p.stock<5?'text-[var(--accent)]':''}`}>{p.stock>0?`${p.stock} left`:'SOLD OUT'}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
      {products.length === 0 && (
        <div className="text-center py-20 font-mono" data-testid="empty-state">
          <p className="mb-4">No inventory matches {activeSearch ? <>search "<strong>{activeSearch}</strong>"</> : "this filter"}.</p>
          <button onClick={()=>{ pickCategory("All"); }} className="btn-outline" data-testid="reset-filters-btn">RESET FILTERS</button>
        </div>
      )}
    </div>
  );
}
